import * as React from "react"
import { Check } from "lucide-react"
import { useIntl } from "react-intl"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { CHOICE_LETTERS } from "./format"
import type { Question } from "@/types/quizzes"

export function QuestionList({
  questions,
  actions,
}: {
  questions: Question[]
  actions?: (question: Question) => React.ReactNode
}) {
  const intl = useIntl()
  return (
    <ol className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page p-0">
      {questions.map((question, index) => (
        <li key={question.id} className="flex items-start gap-4 px-4 py-4">
          <span className="w-6 shrink-0 pt-0.5 text-right text-xs text-ink-muted">{index + 1}</span>
          <div className="min-w-0 flex-1">
            <div className="whitespace-pre-wrap break-words text-sm font-medium text-ink-display">{question.text}</div>
            <ul className="mt-2 space-y-1 p-0">
              {question.choices.map((choice, i) => {
                const correct = i === question.correct_choice
                return (
                  <li
                    key={i}
                    className={cn("flex list-none items-start gap-2 text-sm", correct ? "text-accent-display" : "text-ink-body")}
                  >
                    <span className="w-4 shrink-0 text-ink-muted">{CHOICE_LETTERS[i]}</span>
                    <span className="min-w-0 break-words">{choice}</span>
                    {correct && <Check className="mt-0.5 h-4 w-4 shrink-0" aria-label={intl.formatMessage({ id: "quizzes.correct_aria" })} />}
                  </li>
                )
              })}
            </ul>
            {question.explanation && (
              <div className="mt-2 whitespace-pre-wrap break-words text-xs text-ink-muted">{question.explanation}</div>
            )}
            {question.content_area_name && (
              <Badge tone="muted" className="mt-2">
                {question.content_area_name}
              </Badge>
            )}
          </div>
          {actions?.(question)}
        </li>
      ))}
    </ol>
  )
}
